<template>
  <div class="form-group" role="group">
    <label v-if="label" :for="id" class="form-label">
      {{ label }}
      <span v-if="required" class="required-asterisk">*</span>
    </label>
    <div class="form-select-wrap mt-2">
      <select
        :id="id"
        ref="selectRef"
        v-model="model"
        v-bind="attrs"
        class="form-input"
        :class="{ 'form-input--error': !!error }"
        :disabled="disabled"
        :aria-label="label"
        :aria-required="required"
        :aria-invalid="!!error"
        :aria-describedby="error ? `${id}-error` : undefined"
        :autocomplete="autocomplete"
      >
        <slot></slot>
      </select>
      <ChevronDownIcon class="form-select-chevron" aria-hidden="true" />
    </div>

    <span v-if="error" :id="`${id}-error`" class="form-error" role="alert">
      {{ error }}
    </span>
  </div>
</template>

<script setup lang="ts">
import ChevronDownIcon from '~/assets/icons/chevron-down.svg?component'
import { useUniqueId } from '~/composables/useUniqueId'

defineOptions({ inheritAttrs: false })

const attrs = useAttrs()

const {
  label = undefined,
  disabled = false,
  id = useUniqueId('select').value,
  required = false,
  error = undefined,
  autocomplete = undefined,
} = defineProps<{
  label?: string
  disabled?: boolean
  id?: string
  required?: boolean
  error?: string
  autocomplete?: string
}>()

const model = defineModel<string>()
const selectRef = ref<HTMLSelectElement | null>(null)

const focusSelect = () => {
  if (selectRef.value) {
    selectRef.value.focus()
  }
}

defineExpose({
  focusSelect,
})
</script>

<style lang="postcss" scoped>
.form-group {
  @apply space-y-1;
}

.form-label {
  @apply block text-sm font-light text-gray-700;
}

.form-select-wrap {
  @apply relative;
}

.form-input {
  @apply w-full rounded-lg pl-2 pr-8 h-10;
  @apply bg-white appearance-none;
  @apply border border-gray-200;
  @apply focus:outline focus:outline-1 focus:outline-primary hover:border-gray-400;
  @apply transition-all duration-200 ease-in-out;

  &:focus {
    box-shadow:
      0px 1px 1px rgba(0, 0, 0, 0.03),
      0px 3px 6px rgba(0, 0, 0, 0.02);
  }
}

.form-select-chevron {
  @apply pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500;
}

.form-input--error {
  @apply border-red focus:outline-red hover:border-red;
}

.form-error {
  @apply text-sm text-red;
}

.required-asterisk {
  @apply text-red ml-0.5;
}
</style>
